export type StreamingMode = 'EXTERNAL_STREAM' | 'RFW_MANAGED';

export interface RadioFormData {
  name: string;
  slogan: string;
  description: string;
  category: string;
  country: string;
  city: string;
  streamUrl: string;
  streamType: string;
  bitrate: string;
  /** Nombre del proveedor de streaming (solo EXTERNAL_STREAM) */
  providerName: string;
  /** Formato/codec del stream (solo EXTERNAL_STREAM) */
  streamFormat: string;
  logoFile: File | null;
  logoPreview: string;
  accentColor: string;
  planId: string;
  frequency: string;
  specialFrequency: string;
  acceptedTerms: boolean;
  /** Modalidad de streaming: EXTERNAL_STREAM o RFW_MANAGED */
  streamingMode: StreamingMode;
}

export const STEPS = [
  { id: 1, label: 'Datos de la radio', icon: 'ri-radio-line' },
  { id: 2, label: 'Modalidad de Streaming', icon: 'ri-sound-module-line' },
  { id: 3, label: 'Logo y branding', icon: 'ri-palette-line' },
  { id: 4, label: 'Plan', icon: 'ri-vip-crown-line' },
  { id: 5, label: 'Revisión legal', icon: 'ri-check-double-line' },
] as const;

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
export const MAX_FILE_SIZE_MB = 10;

/** Dominios y patrones bloqueados para stream_url */
export const BLOCKED_URL_PATTERNS = [
  /^https?:\/\/localhost/i,
  /^https?:\/\/127\.\d+\.\d+\.\d+/i,
  /^https?:\/\/192\.168\.\d+\.\d+/i,
  /^https?:\/\/10\.\d+\.\d+\.\d+/i,
  /^https?:\/\/172\.(1[6-9]|2\d|3[01])\.\d+\.\d+/i,
  /^https?:\/\/0\.0\.0\.0/i,
  /^https?:\/\/\[::1\]/i,
];

/** Patrones de credenciales incrustadas en URL */
export const CREDENTIAL_URL_PATTERNS = [
  /\/\/[^:@/\s]+:[^@/\s]+@/i,
  /[?&](password|pass|pwd|token|key|secret)=[^&\s]+/i,
];

export function generateSuggestedFrequency(): string {
  const whole = Math.floor(Math.random() * 40) + 70;
  const decimal = Math.floor(Math.random() * 89) + 10;
  return `${whole}.${decimal}`;
}

/** Validar URL de stream: formato, protocolos permitidos, sin credenciales, sin redes privadas */
export function validateStreamUrl(url: string): { valid: boolean; error?: string } {
  const trimmed = url.trim();
  if (!trimmed) return { valid: true };

  if (!/^https?:\/\/.+/.test(trimmed)) {
    return { valid: false, error: 'La URL debe comenzar con http:// o https://' };
  }

  try {
    const parsed = new URL(trimmed);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { valid: false, error: 'Solo se permiten protocolos http:// y https://' };
    }

    for (const pattern of BLOCKED_URL_PATTERNS) {
      if (pattern.test(trimmed)) {
        return { valid: false, error: 'No se permiten URLs de redes locales o privadas' };
      }
    }

    for (const pattern of CREDENTIAL_URL_PATTERNS) {
      if (pattern.test(trimmed)) {
        return { valid: false, error: 'No se permiten credenciales incrustadas en la URL' };
      }
    }

    // Bloquear URLs que claramente son paneles administrativos
    const adminKeywords = ['/admin', '/panel', '/login', '/config', '/dashboard', '/cp'];
    const pathLower = parsed.pathname.toLowerCase();
    for (const kw of adminKeywords) {
      if (pathLower.includes(kw)) {
        return { valid: false, error: 'La URL parece ser un panel administrativo, no una Stream URL pública' };
      }
    }

    return { valid: true };
  } catch {
    return { valid: false, error: 'URL inválida. Verificá el formato.' };
  }
}